// Media primitives shared by Work and Journal: click-to-load video embeds
// (no YouTube iframes until someone asks for one), plain video files, GIFs
// that only animate on hover, and a keyboard/swipe-friendly lightbox.
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, IconButton, Image, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ACCENT, BG, EASE, FG, GRID, INSET, MONO, MUTED, alpha } from "../theme";

const MotionBox = motion(Box);

// "https://youtu.be/ID", "youtube.com/watch?v=ID", "/shorts/ID", a bare ID,
// a Vimeo URL, or a video file path → { kind, id | src }
export const parseVideo = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const yt = s.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([\w-]{11})/);
  if (yt) return { kind: "youtube", id: yt[1] };
  if (/^[\w-]{11}$/.test(s)) return { kind: "youtube", id: s };
  const vm = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "vimeo", id: vm[1] };
  return { kind: "file", src: s };
};

export const youtubeThumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

const PlayButton = ({ size = 72 }) => (
  <Flex
    className="play-btn"
    w={`${size}px`}
    h={`${size}px`}
    borderRadius="full"
    bg={ACCENT}
    color={BG}
    align="center"
    justify="center"
    boxShadow={`0 10px 40px ${alpha("#000000", 0.5)}`}
    transition="transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)"
  >
    <svg width={size * 0.32} height={size * 0.32} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" />
    </svg>
  </Flex>
);

// 16:9 frame that shows a poster + play button and swaps in the player on click
export const VideoEmbed = ({ video, poster, title = "Video", ratio = 16 / 9, autoPlay = false, radius = "xl" }) => {
  const [playing, setPlaying] = useState(autoPlay);
  const v = typeof video === "string" ? parseVideo(video) : video;
  useEffect(() => setPlaying(autoPlay), [v && (v.id || v.src), autoPlay]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v) return null;

  const thumb = poster || (v.kind === "youtube" ? youtubeThumb(v.id) : null);
  const frame = {
    position: "relative",
    w: "100%",
    pb: `${100 / ratio}%`,
    bg: INSET,
    borderRadius: radius,
    overflow: "hidden",
  };

  if (v.kind === "file") {
    return (
      <Box {...frame}>
        <Box
          as="video"
          src={v.src}
          poster={thumb || undefined}
          controls
          playsInline
          preload="metadata"
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          objectFit="contain"
          bg="#000"
        />
      </Box>
    );
  }

  if (playing) {
    const src =
      v.kind === "youtube"
        ? `https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
        : `https://player.vimeo.com/video/${v.id}?autoplay=1&dnt=1`;
    return (
      <Box {...frame}>
        <Box
          as="iframe"
          src={src}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          border="0"
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
        />
      </Box>
    );
  }

  return (
    <Box
      as="button"
      type="button"
      aria-label={`Play: ${title}`}
      onClick={() => setPlaying(true)}
      display="block"
      textAlign="left"
      cursor="pointer"
      role="group"
      sx={{ "&:hover .play-btn": { transform: "scale(1.08)" }, "&:hover img": { transform: "scale(1.03)" } }}
      {...frame}
    >
      {thumb && (
        <Image
          src={thumb}
          alt=""
          loading="lazy"
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          objectFit="cover"
          transition="transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)"
        />
      )}
      <Box position="absolute" inset={0} bgGradient={`linear(to-t, ${alpha(BG, 0.75)}, transparent 55%)`} />
      <Flex position="absolute" inset={0} align="center" justify="center">
        <PlayButton />
      </Flex>
      <Text
        position="absolute"
        left={4}
        bottom={3}
        right={4}
        fontFamily={MONO}
        fontSize="10px"
        letterSpacing="0.12em"
        textTransform="uppercase"
        color={FG}
        noOfLines={1}
      >
        ▶ {v.kind === "vimeo" ? "Vimeo" : "YouTube"} · {title}
      </Text>
    </Box>
  );
};

// a still that turns into its GIF on hover (or always, when `play` is set)
export const HoverGif = ({ src, poster, alt = "", play, fit = "cover", ...rest }) => {
  const [hover, setHover] = useState(false);
  const animate = play || hover;
  return (
    <Box
      position="relative"
      w="100%"
      h="100%"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      <Image src={poster || src} alt={alt} loading="lazy" w="100%" h="100%" objectFit={fit} />
      {src && poster && animate && (
        <Image src={src} alt="" position="absolute" inset={0} w="100%" h="100%" objectFit={fit} />
      )}
    </Box>
  );
};

const ArrowBtn = ({ dir, onClick }) => (
  <IconButton
    aria-label={dir < 0 ? "Previous" : "Next"}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    icon={<Text fontSize="xl">{dir < 0 ? "←" : "→"}</Text>}
    position="absolute"
    top="50%"
    transform="translateY(-50%)"
    {...(dir < 0 ? { left: { base: 2, md: 6 } } : { right: { base: 2, md: 6 } })}
    borderRadius="full"
    w="52px"
    h="52px"
    bg={alpha("#1b1b1f", 0.8)}
    color={FG}
    border={`1px solid ${GRID}`}
    _hover={{ bg: ACCENT, color: BG }}
    zIndex={2}
  />
);

// items: [{ src, caption, meta }]
export const Lightbox = ({ items, index, onClose, onIndex }) => {
  const open = index != null && items[index];
  const touch = useRef(null);
  const go = useCallback(
    (d) => onIndex((index + d + items.length) % items.length),
    [index, items.length, onIndex]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && items.length > 1) go(1);
      if (e.key === "ArrowLeft" && items.length > 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, go, onClose, items.length]);

  return (
    <AnimatePresence>
      {open && (
        <MotionBox
          position="fixed"
          inset={0}
          zIndex={2000}
          bg={alpha(BG, 0.96)}
          backdropFilter="blur(10px)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          onTouchStart={(e) => {
            touch.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            if (touch.current == null || items.length < 2) return;
            const dx = e.changedTouches[0].clientX - touch.current;
            if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
            touch.current = null;
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            justify="space-between"
            align="center"
            px={{ base: 4, md: 8 }}
            py={4}
            zIndex={2}
          >
            <Text fontFamily={MONO} fontSize="xs" color={MUTED} letterSpacing="0.12em">
              {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </Text>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              icon={<Text fontSize="lg">✕</Text>}
              borderRadius="full"
              bg="transparent"
              color={FG}
              border={`1px solid ${GRID}`}
              _hover={{ bg: FG, color: BG }}
            />
          </Flex>
          <AnimatePresence mode="wait">
            <MotionBox
              key={index}
              position="absolute"
              inset={{ base: "72px 12px 96px", md: "80px 110px 110px" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <Image
                src={items[index].src}
                alt={items[index].caption || ""}
                maxW="100%"
                maxH="100%"
                objectFit="contain"
                borderRadius="lg"
                onClick={(e) => e.stopPropagation()}
              />
            </MotionBox>
          </AnimatePresence>
          {items.length > 1 && (
            <>
              <ArrowBtn dir={-1} onClick={() => go(-1)} />
              <ArrowBtn dir={1} onClick={() => go(1)} />
            </>
          )}
          {(items[index].caption || items[index].meta) && (
            <Box position="absolute" bottom={0} left={0} right={0} px={{ base: 5, md: 8 }} py={6} textAlign="center">
              {items[index].caption && (
                <Text color={FG} fontSize={{ base: "md", md: "lg" }} maxW="720px" mx="auto">
                  {items[index].caption}
                </Text>
              )}
              {items[index].meta && (
                <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.14em" textTransform="uppercase" color={MUTED} mt={1}>
                  {items[index].meta}
                </Text>
              )}
            </Box>
          )}
        </MotionBox>
      )}
    </AnimatePresence>
  );
};
